import React, { useMemo, useState } from "react";
import {
  ArrowRight,
  Mail,
  Gamepad2,
  Home,
  Shirt,
  Coffee,
  Grid2X2,
  Sparkles,
} from "lucide-react";
import Layout from "../components/layout";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../utils/api";
import axios from "axios";

const Container = ({ children, className = "" }) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/60 px-3 py-1 text-xs font-medium text-black/70 backdrop-blur">
    {children}
  </span>
);

const PrimaryButton = ({ children, className = "", ...props }) => (
  <Link
    className={`inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </Link>
);

const GhostButton = ({ children, className = "", ...props }) => (
  <Link
    className={`inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-2 text-sm font-semibold text-black/80 shadow-sm transition hover:bg-black/5 active:scale-[0.98] ${className}`}
    {...props}
  >
    {children}
  </Link>
);

const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="text-lg font-extrabold tracking-tight text-orange-600 sm:text-xl">
      {value}
    </div>
    <div className="mt-1 text-xs font-medium text-black/55">{label}</div>
  </div>
);

const Badge = ({ children, tone = "new" }) => {
  const styles =
    tone === "sale" ? "bg-red-600 text-white" : "bg-emerald-600 text-white";
  return (
    <span
      className={`absolute left-3 top-3 rounded-lg px-2 py-1 text-[11px] font-bold uppercase tracking-wide ${styles}`}
    >
      {children}
    </span>
  );
};

const ProductCard = ({ p }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(`/product/${p.slug}`)}
      className="group cursor-pointer overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm transition hover:shadow-md"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-black/5">
        {p.badge && (
          <Badge tone={p.badge === "Sale" ? "sale" : "new"}>{p.badge}</Badge>
        )}
        <img
          src={p.img}
          alt={p.title}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]"
          loading="lazy"
        />
      </div>

      <div className="space-y-2 p-4">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-black/45">
          {p.category}
        </div>

        <div className="line-clamp-1 text-sm font-extrabold text-black/85">
          {p.title}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm font-extrabold text-orange-600">
            ৳{p.discountPrice || p.basePrice}
          </span>
          {p.discountPrice ? (
            <span className="text-xs font-semibold text-black/40 line-through">
              ৳{p.basePrice}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );

  const CategoryCard = ({ c }) => (
    <button className="group flex w-full items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-4 text-left shadow-sm transition hover:bg-black/5">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-black/5">
        <c.Icon className="h-5 w-5 text-black/70" />
      </span>

      <div className="min-w-0">
        <div className="text-sm font-extrabold text-black/80">{c.title}</div>
        <div className="mt-0.5 line-clamp-1 text-xs font-medium text-black/55">
          {c.subtitle}
        </div>
      </div>

      <span className="ml-auto text-xs font-bold text-orange-600">
        {c.count} items
      </span>
    </button>
  );
};

export default function HomePage() {
  const [products, setProducts] = useState([]);

  React.useEffect(() => {
    // Fetch featured products from the server
    axios
      .get(`${API}/products/get-featured`)
      .then((res) => res.data)
      .then((data) => setProducts(data))
      .catch((err) => console.error("Error fetching featured products:", err));
  }, []);

  const categories = useMemo(
    () => [
      {
        title: "Gaming Collectibles",
        subtitle: "Relive the golden era",
        count: 124,
        Icon: Gamepad2,
      },
      {
        title: "Home & Living",
        subtitle: "Retro vibes for your space",
        count: 89,
        Icon: Home,
      },
      {
        title: "Apparel",
        subtitle: "Wear your nostalgia",
        count: 67,
        Icon: Shirt,
      },
      {
        title: "Drinkware",
        subtitle: "Sip in style",
        count: 42,
        Icon: Coffee,
      },
      {
        title: "Wall Art",
        subtitle: "Pixel perfect decor",
        count: 58,
        Icon: Grid2X2,
      },
      {
        title: "Accessories",
        subtitle: "Complete your look",
        count: 95,
        Icon: Sparkles,
      },
    ],
    []
  );

  return (
    <div className="min-h-screen bg-[#fbf7f1] text-black/90">
      <Layout>
        {/* HERO */}
        <section className="relative overflow-hidden">
          {/* subtle floating shapes */}
          <div className="pointer-events-none absolute left-6 top-12 h-16 w-16 rotate-12 rounded-2xl border-2 border-orange-400/30" />
          <div className="pointer-events-none absolute right-10 top-24 h-14 w-14 -rotate-12 rounded-2xl border-2 border-emerald-400/25" />
          <div className="pointer-events-none absolute right-24 top-64 h-10 w-10 rounded-full bg-orange-600/10" />

          <Container className="py-12 sm:py-16">
            <div className="mx-auto max-w-2xl text-center">
              <Pill>Level Up Your Nostalgia</Pill>

              <h1 className="mt-5 text-4xl font-black leading-[1.05] tracking-tight text-black/90 sm:text-5xl">
                Revive Your Favorite
                <span className="block text-orange-600">Retro Memories</span>
              </h1>

              <p className="mx-auto mt-4 max-w-xl text-sm font-medium leading-relaxed text-black/60 sm:text-base">
                Discover gifts, flowers, and cards inspired by the timeless
                vibes.
              </p>

              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <PrimaryButton to="/products">
                  Shop Collection <ArrowRight className="h-4 w-4" />
                </PrimaryButton>
                <GhostButton to="/about-us">About Us</GhostButton>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-3 rounded-2xl border border-black/10 bg-white/60 p-5 backdrop-blur sm:gap-8 sm:p-6">
                <Stat value="40+" label="Unique Items" />
                <Stat value="2K+" label="Happy Customers" />
                <Stat value="100%" label="Authentic Items" />
              </div>
            </div>
          </Container>
        </section>

        {/* FEATURED PRODUCTS */}
        <section id="featured" className="py-12 sm:py-16">
          <Container>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-black tracking-tight text-black/85">
                  Featured Products
                </h2>
                <p className="mt-1 text-sm font-medium text-black/55">
                  Handpicked treasures from gaming’s golden era. Each tells a
                  story.
                </p>
              </div>

              <GhostButton to="/products" className="w-full sm:w-auto">
                View All Products <ArrowRight className="h-4 w-4" />
              </GhostButton>
            </div>

            <div className="mt-8 grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-4">
              {products.map((p) => (
                <ProductCard key={p.id} p={p} />
              ))}
            </div>
          </Container>
        </section>

        {/* SHOP BY CATEGORY */}
        {/* <section id="categories" className="py-12 sm:py-16">
          <Container>
            <div className="text-center">
              <h2 className="text-2xl font-black tracking-tight text-black/85">
                Shop By Category
              </h2>
              <p className="mt-1 text-sm font-medium text-black/55">
                Find exactly what you’re looking for in our curated collections
              </p>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((c) => (
                <CategoryCard key={c.title} c={c} />
              ))}
            </div>
          </Container>
        </section> */}

        {/* NEWSLETTER */}
        <section className="py-12 sm:py-16">
          <Container>
            <div className="overflow-hidden rounded-3xl bg-[#1f2a3a] px-6 py-10 text-white shadow-sm sm:px-10 sm:py-12">
              <div className="mx-auto max-w-2xl text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-white/10">
                  <Mail className="h-6 w-6" />
                </div>

                <h3 className="mt-4 text-2xl font-black tracking-tight">
                  Join the Retro Crew
                </h3>
                <p className="mx-auto mt-2 max-w-xl text-sm font-medium text-white/70">
                  Get exclusive drops, early access to new arrivals, and special
                  discounts. No spam, just nostalgia delivered to your inbox.
                </p>

                <form
                  className="mx-auto mt-6 flex max-w-xl flex-col gap-3 sm:flex-row"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <input
                    type="email"
                    required
                    placeholder="Enter your email"
                    className="h-11 w-full rounded-xl border border-white/10 bg-white/10 px-4 text-sm outline-none placeholder:text-white/40 focus:ring-2 focus:ring-white/20"
                  />
                  <PrimaryButton className="h-11 bg-orange-600 hover:bg-orange-700">
                    Subscribe
                  </PrimaryButton>
                </form>

                <div className="mt-3 text-xs font-medium text-white/55">
                  Join 5,000+ retro enthusiasts. Unsubscribe anytime.
                </div>
              </div>
            </div>
          </Container>
        </section>
      </Layout>
    </div>
  );
}

function FooterCol({ title, links }) {
  return (
    <div>
      <div className="text-sm font-extrabold text-black/80">{title}</div>
      <ul className="mt-3 space-y-2">
        {links.map((l) => (
          <li key={l}>
            <a
              href="#"
              className="text-sm font-medium text-black/55 transition hover:text-black/80"
            >
              {l}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
